import React, { useEffect, useRef, useState, useCallback } from 'react';
import { injectIntl, useIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import { Field, reduxForm, change } from 'redux-form';
import { connect } from 'react-redux';
import Autosuggest from 'react-autosuggest';
import messages from '../../../locale/messages';
import { match } from '../../../helpers/match';
import history from '../../../history';
import { encode } from '../../../helpers/queryEncryption';
import { openHeaderModal } from '../../../actions/siteadmin/modalActions';
import { setRuntimeVariable } from '../../../actions/runtime';
import { CategorySearchIcon } from '../../Common/Assets/SVGIcon/CommonSvgIcon';
import cs from '../../../styles/autoSuggestSearch.css';
import s from './CategorySearch.css';

const suggestCustomClass = {
    container: 'customAutoSuggestContainer',
    input: 'customAutoSuggestInput',
    suggestionsContainer: 'customAutoSuggestionBox',
    suggestionsList: 'customAutoSuggestListContainer',
    suggestion: 'customAutoSuggestList',
    suggestionHighlighted: 'customAutoSuggestListHighlight'
};

const parse = require('autosuggest-highlight/parse');

function CategorySearch(props) {
    const {
        categoryValue,
        categories: propsCategories,
        isAuthenticated,
        change,
        openHeaderModal,
        setRuntimeVariable,
        isHomePage,
        containerClass,
        handleSubmit,
        placeholder: placeholderProp
    } = props;

    const { formatMessage } = useIntl();
    const [value, setValue] = useState(props.categoryValue && props.categoryValue !== 'all' ? props.categoryValue : '');
    const [localCategories, setLocalCategories] = useState([]);
    const [prevValues, setPrevValues] = useState(null);
    const [isFocused, setIsFocused] = useState(false)

    useEffect(() => {
        if (propsCategories !== undefined) {
            setLocalCategories(propsCategories);
        }
    }, [propsCategories]);

    useEffect(() => {
        if (categoryValue && categoryValue !== 'all') {
            change('category', categoryValue);
        }
    }, [categoryValue, change]);

    const escapeRegexCharacters = (str) => {
        if (str) {
            return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        }
        return '';
    };

    const getSuggestions = (value) => {
        const escapedValue = escapeRegexCharacters(value.trim());
        let filteredOption = [];

        localCategories?.forEach(item => {
            if (item && item.id && item.name) {
                filteredOption.push({
                    id: item.id,
                    name: item.name,
                });
            }
        });

        if (escapedValue === '') {
            return filteredOption.sort(() => 0.5 - Math.random()).slice(0, 5);
        }

        const regex = new RegExp(escapedValue, 'i');
        return filteredOption.filter(item => regex.test(item.name));
    };

    const getSuggestionValue = (suggestion) => {
        return suggestion?.name;
    }

    const renderSuggestion = (suggestion, { query }) => {
        const suggestionText = `${suggestion?.name || ''}`;
        const matches = match(suggestionText, query, true);
        const parts = parse(suggestionText, matches);

        return (
            <div>
                {parts.map((part, index) =>
                    part.highlight ? (
                        <span className={s.highlight} key={index}>{part.text}</span>
                    ) : (
                        <span key={index}>{part.text}</span>
                    )
                )}
            </div>
        );
    };

    const renderSuggestionsContainer = ({ containerProps, children }) => {
        return <div {...containerProps}>{children}</div>;
    }

    const getSelectedValue = async (event, { suggestion }) => {
        await change('categoryId', suggestion.id);
        await change('categoryName', suggestion.name);

        // Guests search + browse the whole funnel logged-out (guest-cart flow); login is deferred to
        // "Review Your Request". Navigate everyone into the category instead of walling guests here.
        history.push(`/category?type=${encodeURIComponent(encode(suggestion?.id?.toString()))}`);
        await change('categoryName', null);
        window.scrollTo({ top: 0, left: 0 });
    };

    // Optional `placeholder` prop overrides the i18n default (used by the home hero for its rotating
    // "describe your problem" prompts). Additive — other usages keep the i18n placeholder.
    const placeholder = placeholderProp || (isHomePage ?
        formatMessage(messages.categorySearch) :
        formatMessage(messages.searchCategory));

    const renderAutosuggestField = ({ input, meta, ...fieldProps }) => {
        const [suggestions, setSuggestions] = useState([]);
        const [noSuggestions, setNoSuggestions] = useState(false);
        const inputRef = useRef(null);

        useEffect(() => {
            if (isFocused && inputRef.current) {
                inputRef.current.focus();
            }
        }, [isFocused]);

        const onSuggestionsFetchRequested = ({ value }) => {
            const result = getSuggestions(value);
            const isInputBlank = value.trim() === '';
            const hasNoSuggestions = !isInputBlank && result.length === 0;
            setSuggestions(result);
            setNoSuggestions(hasNoSuggestions);
        }

        const onSuggestionsClearRequested = () => {
            setSuggestions([]);
        }

        const onChange = async (event, { newValue }) => {
            setValue(newValue);
            input.onChange(newValue);
            let isInputBlank = newValue.trim() === '';
            if (isInputBlank) {
                await change('categoryId', null);
                await change('categoryName', null);
                if (!isHomePage) {
                    location !== '/' && history.push('/category?type=all');
                    location !== '/' && window.scrollTo({
                        top: 0,
                        left: 0
                    });
                }
            }

        };

        const onFocus = (event) => {
            setIsFocused(true)
            if (input) {
                input.onFocus(event);
            }
        }
        const onBlur = () => {
            setIsFocused(false);
        };


        const shouldRenderSuggestions = (value) => {
            return value.trim().length >= 0;
        };

        const storeInputReference = (autosuggest) => {
            if (autosuggest?.input) {
                inputRef.current = autosuggest.input;
            } else if (isFocused) {
                inputRef.current.focus()
            }
        }

        const handleKeyDown = async (event) => {
            if (event?.key === 'Enter') {
                event.preventDefault();

                if (value.trim() === (prevValues && prevValues.trim())) return;

                setPrevValues(value);
                history.push(`/category?type=${value && value.trim() !== '' ? value.trim() : 'all'}`);
                window.scrollTo({ top: 0, left: 0 });

                const location = history?.location?.pathname;
                if (location !== '/') {
                    await change('categoryName', null);
                }
            }
        };

        return (
            <Autosuggest
                suggestions={suggestions}
                onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                onSuggestionsClearRequested={onSuggestionsClearRequested}
                getSuggestionValue={getSuggestionValue}
                renderSuggestion={renderSuggestion}
                renderSuggestionsContainer={renderSuggestionsContainer}
                inputProps={{
                    ...input,
                    placeholder: fieldProps.placeholder || placeholder,
                    value: value,
                    onChange: onChange,
                    onFocus: onFocus,
                    onBlur: onBlur,
                    maxLength: 250,
                    onKeyDown: handleKeyDown,
                }}
                onSuggestionSelected={getSelectedValue}
                focusInputOnSuggestionClick={false}
                shouldRenderSuggestions={shouldRenderSuggestions}
                theme={suggestCustomClass}
                ref={storeInputReference}
            />
        );
    };

    const handleClick = async () => {
        let location = history?.location?.pathname;

        if (value.trim() === (prevValues && prevValues.trim())) return;

        setPrevValues(value.trim());

        history.push(`/category?type=${value && value.trim() !== '' ? value.trim() : 'all'}`);
        window.scrollTo({ top: 0, left: 0 });

        if (location !== '/') {
            await change('categoryName', null);
        }
    }
    return (
        <form onSubmit={handleSubmit}>
            <div className={cx(s.categoryInputFlex, containerClass)}>
                <Field
                    name="category"
                    component={renderAutosuggestField}
                    placeholder={placeholder}
                    className={s.formInputLoading}
                />
                <div
                    className={cx(s.categorySearchBtn, 'categorySearchBtnRTL')}
                    onClick={handleClick}
                    onKeyDown={(e) => e.key === 'Enter' && handleClick(e)}
                >
                    <CategorySearchIcon />
                </div>
            </div>
        </form>
    );
}


const ConnectedCategorySearch = reduxForm({
    form: 'SearchForm',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
})(CategorySearch);

const mapState = (state) => ({
    categories: state.categories.data,
    isAuthenticated: state?.runtime?.isAuthenticated
});

const mapDispatch = {
    change,
    openHeaderModal,
    setRuntimeVariable
};

export default injectIntl(withStyles(cs, s)(connect(mapState, mapDispatch)(ConnectedCategorySearch)));